import { RouteUrl } from "@/configs/router/routes";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ExportToExcelButton } from "@/components/general/common/ExportToExcelButton";
import { exportMatchesKpisToExcel, exportPlayerComparisonByMatchesToExcel } from "@/api/excelExport";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { useSelectedTeam } from "@/context/teamContext";
import { getPeriodLabel } from "./PeriodData/periodDataUtils";
import { ONBOARDING_IDS } from "@/components/general/Onboarding/constants";

const navItems = [
    { to: RouteUrl.DashboardOverview, label: "Overview" },
    { to: RouteUrl.DashboardPeriodData, label: "Period Data" },
    { to: RouteUrl.DashboardAnalytics, label: "Analytics" },
    { to: RouteUrl.DashboardHeatMap, label: "Heat Map" },
    { to: RouteUrl.DashboardCompare, label: "Compare" },
    { to: RouteUrl.DashboardTeamComparison, label: "Teams" },
];

interface DashboardNavigationProps {
    viewMode?: "average" | "total" | "line" | "period";
    onViewModeChange?: (mode: "average" | "total" | "line" | "period") => void;
    selectedPlayers?: Array<{ playerId: number; firstName: string; lastName: string }>;
    matchIds?: number[];
    periodNumbers?: number[];
    selectedPeriod?: number | "all";
    onPeriodChange?: (period: number | "all") => void;
}

const DashboardNavigation = ({
    viewMode = "average",
    onViewModeChange,
    selectedPlayers = [],
    matchIds = [],
    periodNumbers = [],
    selectedPeriod = "all",
    onPeriodChange,
}: DashboardNavigationProps) => {
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const navigate = useNavigate();

    const showViewModeSwitch = pathname === RouteUrl.DashboardAnalytics || pathname === RouteUrl.DashboardCompare;
    const showPeriodSwitch = pathname === RouteUrl.DashboardPeriodData && periodNumbers.length > 0;

    const isAnalyticsPage = pathname === RouteUrl.DashboardAnalytics;

    const { teamId } = useSelectedTeam();

    return (
        <div id="onboarding-dashboard-nav" className="flex flex-wrap gap-2 mb-1">
            <nav
                className={`rounded-md p-1 bg-surface-container-low basis-full ${showViewModeSwitch || showPeriodSwitch ? "md:basis-0 md:flex-1" : ""}`}
            >
                <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                    {navItems.map((item) => {
                        const isActive = pathname === item.to;
                        const isTeamCompare = item.to === RouteUrl.DashboardTeamComparison;
                        const isPeriodData = item.to === RouteUrl.DashboardPeriodData;
                        const isDisabled =
                            (isTeamCompare && matchIds.length > 1) || (isPeriodData && matchIds.length > 1);

                        const button = (
                            <button
                                key={item.to}
                                type="button"
                                disabled={isDisabled}
                                className={`nav-tab
                                    ${isDisabled ? "opacity-50 cursor-not-allowed!" : ""}
                                    ${isActive ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => {
                                    if (!isActive && !isDisabled) {
                                        navigate({ to: item.to });
                                    }
                                }}
                            >
                                {item.label}
                            </button>
                        );

                        if (isDisabled) {
                            const tooltipText = isPeriodData
                                ? "Select exactly 1 match to view period data"
                                : "The view works only with 1 match selected for team comparison";
                            return (
                                <Tooltip key={item.to}>
                                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                                    <TooltipContent>{tooltipText}</TooltipContent>
                                </Tooltip>
                            );
                        }

                        return button;
                    })}
                </div>
            </nav>
            {showViewModeSwitch && (
                <div className="flex flex-col gap-2 basis-full md:flex-row md:items-center md:basis-0 md:flex-1">
                    {onViewModeChange && (
                        <nav
                            id="onboarding-analytics-view-mode"
                            className="rounded-md p-1 bg-surface-container-low flex-1"
                        >
                            <div className="grid grid-cols-2 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                                <button
                                    type="button"
                                    className={`nav-tab ${viewMode === "average" ? "nav-tab-active" : "nav-tab-inactive"}`}
                                    onClick={() => onViewModeChange("average")}
                                >
                                    Average
                                </button>
                                <button
                                    type="button"
                                    className={`nav-tab ${viewMode === "total" ? "nav-tab-active" : "nav-tab-inactive"}`}
                                    onClick={() => onViewModeChange("total")}
                                >
                                    Total
                                </button>
                                {isAnalyticsPage && (
                                    <button
                                        type="button"
                                        className={`nav-tab ${viewMode === "line" ? "nav-tab-active" : "nav-tab-inactive"}`}
                                        onClick={() => onViewModeChange("line")}
                                    >
                                        Line
                                    </button>
                                )}
                                {isAnalyticsPage &&
                                    (() => {
                                        const isPeriodDisabled = matchIds.length > 1;
                                        const periodBtn = (
                                            <button
                                                type="button"
                                                disabled={isPeriodDisabled}
                                                className={`nav-tab ${isPeriodDisabled ? "opacity-50 cursor-not-allowed!" : ""} ${viewMode === "period" ? "nav-tab-active" : "nav-tab-inactive"}`}
                                                onClick={() => !isPeriodDisabled && onViewModeChange("period")}
                                            >
                                                Period Data
                                            </button>
                                        );
                                        return isPeriodDisabled ? (
                                            <Tooltip key="period">
                                                <TooltipTrigger asChild>{periodBtn}</TooltipTrigger>
                                                <TooltipContent>
                                                    Select exactly 1 match to view period data
                                                </TooltipContent>
                                            </Tooltip>
                                        ) : (
                                            periodBtn
                                        );
                                    })()}
                            </div>
                        </nav>
                    )}
                    <nav className="rounded-md p-1 md:bg-surface-container-low md:shrink-0">
                        <div className="flex items-center justify-center md:justify-end">
                            {pathname === RouteUrl.DashboardCompare ? (
                                <span id="onboarding-analytics-export">
                                    <ExportToExcelButton
                                        mutationFn={() => {
                                            if (!selectedPlayers[0]?.playerId || !selectedPlayers[1]?.playerId) {
                                                throw new Error("Both players must be selected for comparison export");
                                            }
                                            return exportPlayerComparisonByMatchesToExcel(
                                                matchIds,
                                                selectedPlayers[0].playerId,
                                                teamId,
                                                selectedPlayers[1].playerId,
                                                teamId
                                            );
                                        }}
                                        label="Export Comparison to Excel"
                                        successMessage={`Player comparison Excel file for ${selectedPlayers[0]?.firstName} ${selectedPlayers[0]?.lastName} vs ${selectedPlayers[1]?.firstName} ${selectedPlayers[1]?.lastName} across ${matchIds.length === 0 ? "latest match" : matchIds.length === 1 ? "1 match" : `${matchIds.length} matches`} downloaded successfully!`}
                                        disabled={!selectedPlayers[0]?.playerId || !selectedPlayers[1]?.playerId}
                                    />
                                </span>
                            ) : (
                                <span id="onboarding-analytics-export">
                                    <ExportToExcelButton
                                        mutationFn={() => exportMatchesKpisToExcel(matchIds, teamId)}
                                        label="Export Analytics to Excel"
                                        successMessage="Excel file downloaded successfully!"
                                    />
                                </span>
                            )}
                        </div>
                    </nav>
                </div>
            )}
            {showPeriodSwitch && onPeriodChange && (
                <div id={ONBOARDING_IDS.PERIOD_DATA_NAV} className="flex flex-col md:flex-row md:items-center gap-2 flex-1">
                    <nav className="rounded-md p-1 bg-surface-container-low flex-1">
                        <div className="grid grid-cols-2 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            {periodNumbers.map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => onPeriodChange(p)}
                                    className={`nav-tab ${selectedPeriod === p ? "nav-tab-active" : "nav-tab-inactive"}`}
                                >
                                    {getPeriodLabel(p)}
                                </button>
                            ))}
                            <div className="hidden md:block w-px h-4 bg-outline-variant/30 mx-1" />
                            <button
                                type="button"
                                onClick={() => onPeriodChange("all")}
                                className={`nav-tab ${selectedPeriod === "all" ? "nav-tab-active" : "nav-tab-inactive"}`}
                            >
                                All
                            </button>
                        </div>
                    </nav>
                </div>
            )}
        </div>
    );
};

export default DashboardNavigation;
